'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Logout } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@prismo-io/design-system';
import { useSignOut } from '@prismo-io/feature-auth';
import { Application, translate } from '@prismo-io/router';
import { LanguageCode, type LanguageCodeT } from '@prismo-io/schemas';
import { useRouter } from 'next/navigation';
import { type FC, useState } from 'react';
const buildSuccessUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.APP,
    path: `/`,
    locale,
  });
};

export const SignOut: FC = () => {
  const [signOutDialog, setSignOutDialog] = useState<boolean>(false);

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;
  const successUrl = buildSuccessUrl(locale as LanguageCodeT);

  const router = useRouter();

  const { trigger: signOut } = useSignOut();

  const onConfirmSignOut = () => {
    setSignOutDialog(false);
    signOut(undefined, {
      onSuccess() {
        router.push(successUrl);
      },
    });
  };

  const theme = useTheme<Theme>();

  return (
    <>
      <Tooltip title={t`Déconnexion`} placement="top">
        <IconButton onClick={() => setSignOutDialog(true)}>
          <Logout sx={{ color: theme.palette.error.light }} />
        </IconButton>
      </Tooltip>
      <Dialog
        open={signOutDialog}
        onClose={() => setSignOutDialog(false)}
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h2" textAlign="center">
            <Trans>Déconnexion</Trans>
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography textAlign="center">
            <Trans>Souhaitez-vous vraiment vous déconnecter ?</Trans>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Button
              variant="text"
              color="gray"
              onClick={() => setSignOutDialog(false)}
            >
              <Trans>Non</Trans>
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={onConfirmSignOut}
            >
              <Trans>Oui</Trans>
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};
